// Pure thread reducer for the assistant's "status" lines.
//
// The chat stream emits one `tool` event per tool call, and the model often
// calls the same tool several times in a row (a retry, a second search with
// a wider radius). Appending a status entry per event rendered "Searching
// restaurants…" four times in a row before the results. Two rules keep the
// trail readable:
//
//   1. A transient status (a tool in progress) that repeats the trailing
//      transient status with the same label collapses into it, with a count
//      shown as "Searching restaurants… (3)".
//   2. A new transient status REPLACES a trailing transient status with a
//      different label: the previous one is finished by definition — the
//      model moved on to the next tool — so it is not appended as history.
//
// Non-transient statuses ("Added 2 items to your cart") are durable notes and
// are never collapsed or replaced. Anything that is not a status (a bubble,
// cards, a navlink) between two statuses breaks the run, so a status after
// results is a fresh line.
//
// A cart line (a durable status with `cart`) is the result of the tool whose
// transient line sits above it, so it takes that line's place; and the same
// cart line again right after itself collapses into a count. Seven per-member
// updates used to leave seven "Updating your cart… / Updated Burrito Bowl…"
// pairs (bigtest, 2026-10-07).

export const formatStatusLabel = (entry) => {
  const label = String(entry?.label ?? '');
  const count = Number(entry?.count) || 1;
  return count > 1 ? `${label} (${count})` : label;
};

const sameCartLine = (a, b) =>
  a?.type === 'status' &&
  Boolean(a.cart) &&
  !a.transient &&
  a.label === b.label &&
  (a.cart?.cartId ?? null) === (b.cart?.cartId ?? null);

export const appendStatusEntry = (thread, entry) => {
  const list = Array.isArray(thread) ? thread : [];
  const next = { type: 'status', count: 1, ...entry };
  const last = list[list.length - 1];
  const lastIsTransientStatus = last?.type === 'status' && last?.transient === true;
  if (next.cart && !next.transient) {
    const before = lastIsTransientStatus ? list.slice(0, -1) : list;
    const previous = before[before.length - 1];
    if (sameCartLine(previous, next)) {
      return [...before.slice(0, -1), { ...previous, count: (Number(previous.count) || 1) + 1 }];
    }
    return [...before, lastIsTransientStatus ? { ...next, id: last.id } : next];
  }
  if (!next.transient || !lastIsTransientStatus) return [...list, next];
  if (last.label === next.label) {
    const merged = { ...last, count: (Number(last.count) || 1) + 1 };
    return [...list.slice(0, -1), merged];
  }
  // Different tool: the previous one finished. Keep its id so React reuses
  // the row instead of remounting it.
  return [...list.slice(0, -1), { ...next, id: last.id }];
};

// One label per tool the assistant can run that emits a `tool` event: the
// coach tools (dev-server COACH_TOOL_DEFINITIONS + the cart tools) and the
// Phantom tools not in ASSISTANT_BLOCKED_TOOLS. A tool missing here used to
// show its raw name ("Running get deliverable addresses…").
export const TOOL_STATUS_LABELS = Object.freeze({
  // Phantom
  search_restaurants: 'Searching restaurants…',
  search_menu_items: 'Searching menus…',
  list_brand_locations: 'Finding nearby locations…',
  get_restaurant_detail: 'Checking restaurant details…',
  get_menu: 'Getting menu…',
  get_availability_calendar: 'Checking available times…',
  check_delivery_availability: 'Checking delivery availability…',
  validate_order: 'Validating order…',
  quote_order: 'Pricing your order…',
  // Coach account
  get_team_roster: 'Checking your roster…',
  get_saved_locations: 'Looking up saved locations…',
  get_deliverable_addresses: 'Checking delivery addresses…',
  sync_saved_location: 'Setting up your delivery address…',
  get_saved_templates: 'Checking your saved orders…',
  get_team_polls: 'Checking poll results…',
  get_past_orders: 'Reviewing past orders…',
  get_upcoming_events: 'Checking your calendar…',
  create_share_link: 'Creating a share link…',
  contact_support: 'Contacting MealOps support…',
  // Carts
  add_items_to_cart: 'Adding items to your cart…',
  get_cart: 'Checking your cart…',
  update_cart_item: 'Updating your cart…',
  move_cart: 'Moving your cart…',
  delete_cart: 'Checking that cart…',
  // Saves
  save_template: 'Saving your template…',
  // Confirmation cards
  propose_poll: 'Preparing a poll for you to confirm…',
  propose_roster_change: 'Preparing a roster change for you to confirm…',
  propose_saved_location: 'Looking up that address…',
});

// An unknown (newly added) tool gets a neutral line, never its internal name.
export const toolStatusLabel = (name) => TOOL_STATUS_LABELS[name] || 'Working on it…';

// "Sat, Oct 10 at 6:00 PM" for a cart's slot (date YYYY-MM-DD, time HH:MM).
// A date-only value is a calendar day, so it is read and printed in UTC: a
// local parse shifts it a day for every coach west of UTC.
const formatCartSlot = (date, time) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(date ?? ''));
  if (!match) return null;
  const day = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  const dayLabel = day.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' });
  const clock = /^(\d{2}):(\d{2})/.exec(String(time ?? ''));
  if (!clock) return dayLabel;
  const hour = Number(clock[1]);
  const timeLabel = `${hour % 12 || 12}:${clock[2]} ${hour < 12 ? 'AM' : 'PM'}`;
  return `${dayLabel} at ${timeLabel}`;
};

const forMember = (data) => (data?.memberName ? ` for ${data.memberName}` : '');

// The durable status line for a `cart` stream event.
export const cartStatusLabel = (data) => {
  const restaurantName = data?.restaurantName || 'the restaurant';
  switch (data?.action) {
    case 'updated':
      return `Updated ${data?.itemName || 'an item'}${forMember(data)} in your ${restaurantName} cart`;
    case 'removed':
      return `Removed ${data?.itemName || 'an item'}${forMember(data)} from your ${restaurantName} cart`;
    case 'moved': {
      const slot = formatCartSlot(data?.date, data?.time);
      return slot ? `Moved your ${restaurantName} cart to ${slot}` : `Moved your ${restaurantName} cart`;
    }
    case 'deleted':
      // delete_cart sends what the cart held; a cart the assistant's own
      // removals emptied sends no count.
      return Number(data?.itemCount) > 0
        ? `Deleted your ${restaurantName} cart`
        : `Deleted your empty ${restaurantName} cart`;
    default: {
      const count = Number(data?.itemsAdded) || 0;
      return `Added ${count} item${count === 1 ? '' : 's'} to your ${restaurantName} cart`;
    }
  }
};
